import BaseFormController from "../../../../validation/BaseFormController";
import {FormField, OptionalFormField, RequiredFormField} from "../../../../validation/FormField";
import CustomField, {CustomFieldType} from "../../../../model/customField/CustomField";
import {none, option} from "ts-option";
import FormValidation from "../../../../validation/FormValidation";
import {ValidationResult} from "../../../../validation/ValidationResult";

const uuidv4 = require("uuid/v4");

/**
 * Custom field form controller.
 */
export default class CustomFieldForm extends BaseFormController<CustomFieldForm> {
	constructor(formId: string,
		public readonly fieldType: RequiredFormField<CustomFieldType>,
		public readonly label: RequiredFormField<string>,
		public readonly disabledInWorkspace: RequiredFormField<boolean>,
		public readonly disabledInUi: RequiredFormField<boolean>,
		public readonly options: OptionalFormField<string[]>) {
		super(fieldType, formId);
	}

	static createCustomFieldForm({
		fieldType = none,
		label = none,
		disabledInWorkspace = none,
		disabledInUi = none,
		options = none
	}) {
		const fieldTypeField = RequiredFormField.create<CustomFieldType>(fieldType, "Type is required");
		return new CustomFieldForm(
			uuidv4(),
			fieldTypeField,
			RequiredFormField.create<string>(label, "Label is required"),
			RequiredFormField.create<boolean>(disabledInWorkspace),
			RequiredFormField.create<boolean>(disabledInUi),
			OptionalFormField.create<string[]>(options, [
				CustomFieldForm.atLeastOneIfEnum('At least one enum option required', fieldTypeField.id)
			])
		)
	}

	public static createEmpty(): CustomFieldForm {
		return this.createCustomFieldForm({
			fieldType: option(CustomFieldType.STRING),
			disabledInWorkspace: option(false),
			disabledInUi: option(false),
			options: option([])
		});
	}

	static createFromCustomField(customField: CustomField) {
		return CustomFieldForm.createCustomFieldForm({
			fieldType: option(customField.fieldType),
			label: option(customField.label),
			disabledInWorkspace: option(customField.disabledInWorkspace),
			disabledInUi: option(customField.disabledInUi),
			options: option(customField.options)
		});
	}

	validate(): CustomFieldForm {
		return FormValidation.validateForm<CustomFieldForm>(this, (form, config) => form.copy(config));
	}

	onValid(onValid: (customField: CustomField) => void,
		onInvalid: () => void): ValidationResult<CustomField> {
		return FormValidation.validateFields(
			[this.fieldType, this.label, this.disabledInWorkspace, this.disabledInUi, this.options],
			() => {
				return new CustomField(uuidv4,
					this.fieldType.getValue(),
					this.label.getValue(),
					this.disabledInWorkspace.getValue(),
					this.disabledInUi.getValue(),
					this.options.getOptionalValue().getOrElse([])
				);
			}).onSuccess(onValid).onFailure(onInvalid);
	}

	copy({
		fieldType = this.fieldType,
		label = this.label,
		disabledInWorkspace = this.disabledInWorkspace,
		disabledInUi = this.disabledInUi,
		options = this.options
	}): CustomFieldForm {
		return new CustomFieldForm(this.formId, fieldType, label, disabledInWorkspace, disabledInUi, options);
	}

	fields(): Array<FormField<any>> {
		return [];
	}

	static atLeastOneIfEnum(errorMsg: string, otherFormFieldId: string) {
		return (form: BaseFormController<any>, thisValue: string[]): string[] => {
			const otherFormField: RequiredFormField<CustomFieldType> = form.findFieldById<RequiredFormField<CustomFieldType>>(
				otherFormFieldId).get;
			if (otherFormField.getValue() === CustomFieldType.ENUM && thisValue && thisValue.length > 0 ||
				otherFormField.getValue() !== CustomFieldType.ENUM) {
				return [];
			}
			return [errorMsg];
		}
	}
}